import { useState, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Progress } from "../components/ui/progress";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Upload,
  FileXls,
  ArrowRight,
  Check,
  Warning,
  X,
  DownloadSimple,
  ArrowsClockwise,
  Users,
  CheckCircle,
  XCircle,
  Info,
  CaretRight,
  FileArrowUp,
} from "phosphor-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { id: 1, label: "Upload" },
  { id: 2, label: "Match Columns" },
  { id: 3, label: "Preview & Import" },
];

const STATUS_CONFIG = {
  ready: {
    color: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
    icon: CheckCircle,
    label: "Ready",
  },
  warning: {
    color: "bg-amber-500/15 text-amber-600 border-amber-500/30",
    icon: Warning,
    label: "Warning",
  },
  skipped: {
    color: "bg-zinc-500/15 text-zinc-500 border-zinc-500/30",
    icon: XCircle,
    label: "Skipped",
  },
  duplicate: {
    color: "bg-blue-500/15 text-blue-600 border-blue-500/30",
    icon: Users,
    label: "Duplicate",
  },
  archived_duplicate: {
    color: "bg-purple-500/15 text-purple-600 border-purple-500/30",
    icon: Users,
    label: "Archived Dup",
  },
};

export default function ImportCustomersCard({ onImportComplete }) {
  const [currentStep, setCurrentStep] = useState(1);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [mappings, setMappings] = useState({});
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  // Reset wizard
  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setSelectedFile(null);
    setPreviewData(null);
    setMappings({});
    setImportResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Handle file selection
  const handleFileSelect = useCallback((file) => {
    if (!file) return;

    const validTypes = [
      "text/csv",
      "application/vnd.ms-excel",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    ];
    const extension = file.name.split(".").pop().toLowerCase();

    if (!validTypes.includes(file.type) && !["csv", "xlsx", "xls"].includes(extension)) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx)");
      return;
    }

    setSelectedFile(file);
  }, []);

  // Drag and drop handlers
  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setIsDragging(false);
      const file = e.dataTransfer.files[0];
      handleFileSelect(file);
    },
    [handleFileSelect]
  );

  // Download templates
  const downloadTemplate = async (format) => {
    try {
      const response = await fetch(`${API}/api/customers/import/template.${format}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Download failed");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `customer_import_template.${format}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} template downloaded`);
    } catch {
      toast.error("Failed to download template");
    }
  };

  // Preview import
  const handlePreview = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);

      const response = await fetch(`${API}/api/customers/import/preview`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Preview failed");
      }

      const data = await response.json();
      setPreviewData(data);
      setMappings(data.inferred_mappings || {});
      setCurrentStep(2);
    } catch (error) {
      toast.error(error.message || "Failed to process file");
    } finally {
      setIsLoading(false);
    }
  };

  // Update mapping
  const updateMapping = (column, targetField) => {
    setMappings((prev) => ({
      ...prev,
      [column]: targetField,
    }));
  };

  // Reset mappings to auto-detected
  const resetMappings = () => {
    if (previewData?.inferred_mappings) {
      setMappings(previewData.inferred_mappings);
      toast.success("Mappings reset to automatic");
    }
  };

  // Refresh preview with new mappings
  const refreshPreview = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mappings", JSON.stringify(mappings));

      const response = await fetch(`${API}/api/customers/import/preview`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) throw new Error("Preview failed");

      const data = await response.json();
      setPreviewData(data);
      setCurrentStep(3);
    } catch (error) {
      toast.error("Failed to refresh preview");
    } finally {
      setIsLoading(false);
    }
  };

  // Commit import
  const handleCommit = async () => {
    if (!selectedFile) return;

    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", selectedFile);
      formData.append("mappings", JSON.stringify(mappings));

      const response = await fetch(`${API}/api/customers/import/commit`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Import failed");
      }

      const data = await response.json();
      setImportResult(data);

      if (data.created > 0) {
        toast.success(`${data.created} customers imported successfully!`);
        onImportComplete?.();
      } else if (data.issues?.length > 0) {
        // Show specific reason why nothing was imported
        const firstIssue = data.issues[0];
        toast.error(`Import failed: ${firstIssue.reason || "Unknown error"}`);
      } else if (data.duplicates > 0 && data.created === 0) {
        toast.warning(`All ${data.duplicates} customer(s) were duplicates and skipped`);
      } else {
        toast.warning("No new customers were imported. Check your file data.");
      }
    } catch (error) {
      toast.error(error.message || "Import failed");
    } finally {
      setIsLoading(false);
    }
  };

  // Download issues report
  const downloadIssuesReport = () => {
    if (!previewData?.issues?.length) return;

    const csvContent = [
      ["Row", "Issue"].join(","),
      ...previewData.issues.map((issue) => [issue.row, `"${issue.reason}"`].join(",")),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "import_issues.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    toast.success("Issues report downloaded");
  };

  // Render step indicator
  const renderStepIndicator = () => (
    <div className="flex items-center justify-center gap-2 mb-6">
      {STEPS.map((step, index) => (
        <div key={step.id} className="flex items-center">
          <div
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              currentStep === step.id
                ? "bg-[#FF6600] text-white"
                : currentStep > step.id
                ? "bg-emerald-500/15 text-emerald-600"
                : "bg-zinc-100 text-zinc-400"
            }`}
          >
            {currentStep > step.id ? (
              <Check size={14} weight="bold" />
            ) : (
              <span className="w-4 text-center">{step.id}</span>
            )}
            <span className="hidden sm:inline">{step.label}</span>
          </div>
          {index < STEPS.length - 1 && (
            <CaretRight size={16} className="mx-1 text-zinc-300" />
          )}
        </div>
      ))}
    </div>
  );

  // STEP 1: Upload
  const renderUploadStep = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-zinc-800">Upload your customer file</h3>
        <p className="text-sm text-zinc-500">
          Only a name is required. Missing emails, phones, and addresses are okay.
        </p>
      </div>

      {/* Drop zone */}
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        className={`
          relative cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-all
          ${
            isDragging
              ? "border-[#FF6600] bg-orange-50"
              : selectedFile
              ? "border-emerald-500 bg-emerald-50/50"
              : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
          }
        `}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={(e) => handleFileSelect(e.target.files[0])}
          className="hidden"
        />

        {selectedFile ? (
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <FileXls size={24} className="text-emerald-600" />
            </div>
            <div>
              <p className="font-medium text-emerald-700">File ready: {selectedFile.name}</p>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = "";
                }}
                className="text-sm text-zinc-500 hover:text-zinc-700 underline mt-1"
              >
                Change file
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="mx-auto w-12 h-12 rounded-full bg-zinc-100 flex items-center justify-center">
              <FileArrowUp size={24} className="text-zinc-400" />
            </div>
            <div>
              <p className="font-medium text-zinc-700">
                Drag and drop a CSV or Excel file here, or{" "}
                <span className="text-[#FF6600]">browse</span>
              </p>
              <p className="text-xs text-zinc-400 mt-1">CSV or Excel (.xlsx)</p>
            </div>
          </div>
        )}
      </div>

      {/* Template downloads */}
      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadTemplate("csv")}
          className="w-full sm:w-auto"
        >
          <DownloadSimple size={16} className="mr-2" />
          Download CSV Template
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => downloadTemplate("xlsx")}
          className="w-full sm:w-auto"
        >
          <DownloadSimple size={16} className="mr-2" />
          Download Excel Template
        </Button>
      </div>

      <p className="text-center text-xs text-zinc-400">
        Not sure what format to use? Download a template to get started.
      </p>

      {/* Continue button */}
      {selectedFile && (
        <div className="flex justify-center pt-2">
          <Button
            onClick={handlePreview}
            disabled={isLoading}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-8"
          >
            {isLoading ? (
              <>
                <ArrowsClockwise size={18} className="mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                Continue
                <ArrowRight size={18} className="ml-2" />
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  );

  // STEP 2: Match Columns
  const renderMatchStep = () => (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <h3 className="text-lg font-semibold text-zinc-800">Confirm column matching</h3>
        <p className="text-sm text-zinc-500">
          We&apos;ve matched your columns automatically. Review and adjust if needed.
        </p>
      </div>

      {/* Mapping list */}
      <div className="space-y-3 max-h-[400px] overflow-y-auto pr-2">
        {previewData?.headers?.map((header) => (
          <div
            key={header}
            className="flex items-center gap-3 p-3 rounded-lg bg-zinc-50 border border-zinc-100"
          >
            <div className="flex-1 min-w-0">
              <p className="font-medium text-zinc-700 truncate">{header}</p>
            </div>
            <ArrowRight size={16} className="text-zinc-300 flex-shrink-0" />
            <div className="w-44 flex-shrink-0">
              <Select
                value={mappings[header] || "ignore"}
                onValueChange={(value) => updateMapping(header, value)}
              >
                <SelectTrigger className="h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {previewData?.target_fields?.map((field) => (
                    <SelectItem key={field.value} value={field.value}>
                      {field.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
      </div>

      {/* Reset link */}
      <div className="text-center">
        <button onClick={resetMappings} className="text-sm text-[#FF6600] hover:underline">
          Reset to automatic matching
        </button>
      </div>

      {/* Ignored columns hint */}
      {Object.values(mappings).filter((v) => v === "ignore").length > 0 && (
        <div className="flex items-start gap-2 p-3 rounded-lg bg-zinc-50 text-sm text-zinc-500">
          <Info size={16} className="mt-0.5 flex-shrink-0" />
          <p>Ignored columns will not be imported but will be saved to the Notes field.</p>
        </div>
      )}

      {/* Navigation buttons */}
      <div className="flex justify-between gap-3 pt-2">
        <Button variant="outline" onClick={() => setCurrentStep(1)}>
          Back
        </Button>
        <Button
          onClick={refreshPreview}
          disabled={isLoading}
          className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
        >
          {isLoading ? (
            <>
              <ArrowsClockwise size={18} className="mr-2 animate-spin" />
              Processing...
            </>
          ) : (
            <>
              Continue to Preview
              <ArrowRight size={18} className="ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );

  // STEP 3: Preview & Import
  const renderPreviewStep = () => {
    const counts = previewData?.counts || {};
    const previewRows = previewData?.preview_rows || [];

    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <h3 className="text-lg font-semibold text-zinc-800">Preview your import</h3>
          <p className="text-sm text-zinc-500">
            Review the data below before importing.
          </p>
        </div>

        {/* Summary stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="p-3 rounded-lg bg-zinc-50 text-center">
            <p className="text-2xl font-bold text-zinc-800">{counts.total || 0}</p>
            <p className="text-xs text-zinc-500">Total Rows</p>
          </div>
          <div className="p-3 rounded-lg bg-emerald-50 text-center">
            <p className="text-2xl font-bold text-emerald-600">{counts.ready || 0}</p>
            <p className="text-xs text-emerald-600">Ready</p>
          </div>
          <div className="p-3 rounded-lg bg-amber-50 text-center">
            <p className="text-2xl font-bold text-amber-600">{counts.warnings || 0}</p>
            <p className="text-xs text-amber-600">Warnings</p>
          </div>
          <div className="p-3 rounded-lg bg-blue-50 text-center">
            <p className="text-2xl font-bold text-blue-600">
              {(counts.skipped || 0) + (counts.duplicates || 0)}
            </p>
            <p className="text-xs text-blue-600">Skipped</p>
          </div>
        </div>

        {/* Helper text */}
        <div className="space-y-2">
          {counts.warnings > 0 && (
            <p className="text-sm text-amber-600 flex items-center gap-2">
              <Warning size={16} />
              Warnings won&apos;t block import. You can fix them later.
            </p>
          )}
          {counts.duplicates > 0 && (
            <p className="text-sm text-blue-600 flex items-center gap-2">
              <Users size={16} />
              {counts.duplicates} possible duplicate(s) will be skipped.
            </p>
          )}
        </div>

        {/* Preview list */}
        <div className="border border-zinc-200 rounded-lg overflow-hidden">
          <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200">
            <p className="text-sm font-medium text-zinc-600">
              Preview (showing first {Math.min(previewRows.length, 20)} rows)
            </p>
          </div>
          <ScrollArea className="h-[300px]">
            <div className="divide-y divide-zinc-100">
              {previewRows.map((row, index) => {
                const statusConfig = STATUS_CONFIG[row.status] || STATUS_CONFIG.ready;
                const StatusIcon = statusConfig.icon;

                return (
                  <div
                    key={index}
                    className="px-4 py-3 hover:bg-zinc-50 transition-colors"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-zinc-800 truncate">
                            {row.name || "(No name)"}
                          </p>
                          {row.company && (
                            <span className="text-xs text-zinc-400 truncate">
                              {row.company}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500">
                          {row.email && <span className="truncate">{row.email}</span>}
                          {row.email && row.phone && <span>•</span>}
                          {row.phone && <span>{row.phone}</span>}
                        </div>
                        {row.location && (
                          <p className="text-xs text-zinc-400 mt-0.5">{row.location}</p>
                        )}
                        {row.reason && row.status !== "ready" && (
                          <p className="text-xs text-amber-600 mt-1">{row.reason}</p>
                        )}
                      </div>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Badge
                              variant="outline"
                              className={`flex-shrink-0 ${statusConfig.color}`}
                            >
                              <StatusIcon size={12} className="mr-1" />
                              {statusConfig.label}
                            </Badge>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{row.reason || `Row ${row.row_number} is ${statusConfig.label.toLowerCase()}`}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </div>
                  </div>
                );
              })}
              {previewRows.length === 0 && (
                <div className="px-4 py-8 text-center text-zinc-400">
                  No rows to preview
                </div>
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Download issues button */}
        {previewData?.issues?.length > 0 && (
          <div className="text-center">
            <Button variant="outline" size="sm" onClick={downloadIssuesReport}>
              <DownloadSimple size={16} className="mr-2" />
              Download Issues Report
            </Button>
          </div>
        )}

        {/* Navigation buttons */}
        <div className="flex justify-between gap-3 pt-2">
          <Button variant="outline" onClick={() => setCurrentStep(2)}>
            Back
          </Button>
          <Button
            onClick={handleCommit}
            disabled={isLoading || (counts.ready || 0) === 0}
            className="bg-[#FF6600] hover:bg-[#CC5200] text-white px-8"
          >
            {isLoading ? (
              <>
                <ArrowsClockwise size={18} className="mr-2 animate-spin" />
                Importing...
              </>
            ) : (
              <>
                <Upload size={18} className="mr-2" />
                Import {counts.ready || 0} Customers
              </>
            )}
          </Button>
        </div>
      </div>
    );
  };

  // Success state
  const renderSuccessState = () => (
    <div className="text-center py-8 space-y-6">
      <div className="mx-auto w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
        <CheckCircle size={32} className="text-emerald-600" weight="fill" />
      </div>

      <div className="space-y-2">
        <h3 className="text-xl font-semibold text-zinc-800">Import Complete!</h3>
        <p className="text-zinc-500">
          {importResult?.created} customer{importResult?.created !== 1 ? "s" : ""} added
          {importResult?.skipped > 0 && ` • ${importResult.skipped} skipped`}
          {importResult?.duplicates > 0 && ` • ${importResult.duplicates} duplicates`}
          {importResult?.warnings > 0 && ` • ${importResult.warnings} with warnings`}
        </p>
      </div>

      {/* Stats */}
      <div className="flex justify-center gap-4">
        <div className="text-center">
          <p className="text-2xl font-bold text-emerald-600">{importResult?.created}</p>
          <p className="text-xs text-zinc-500">Created</p>
        </div>
        {importResult?.duplicates > 0 && (
          <div className="text-center">
            <p className="text-2xl font-bold text-blue-600">{importResult.duplicates}</p>
            <p className="text-xs text-zinc-500">Duplicates</p>
          </div>
        )}
        {importResult?.skipped > 0 && (
          <div className="text-center">
            <p className="text-2xl font-bold text-zinc-400">{importResult.skipped}</p>
            <p className="text-xs text-zinc-500">Skipped</p>
          </div>
        )}
      </div>

      {/* Show issues if any rows failed */}
      {importResult?.issues?.length > 0 && importResult.created === 0 && (
        <div className="text-left bg-red-50 p-4 rounded-lg">
          <p className="text-sm font-medium text-red-700 mb-2">Import Issues:</p>
          <ul className="text-sm text-red-600 space-y-1">
            {importResult.issues.slice(0, 5).map((issue, idx) => (
              <li key={idx}>Row {issue.row}: {issue.reason}</li>
            ))}
            {importResult.issues.length > 5 && (
              <li className="text-red-500">...and {importResult.issues.length - 5} more</li>
            )}
          </ul>
        </div>
      )}

      <Button onClick={resetWizard} variant="outline">
        Import More Customers
      </Button>
    </div>
  );

  return (
    <Card className="border-2 border-zinc-200 mt-8" data-testid="import-customers-card">
      <CardHeader
        className="cursor-pointer hover:bg-zinc-50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-100 to-orange-50 flex items-center justify-center">
              <Upload size={20} className="text-[#FF6600]" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold">Import Customers</CardTitle>
              <CardDescription className="text-sm text-zinc-500">
                Bring your customer list in from Excel, Google Sheets, Jobber, Housecall Pro, or QuickBooks.
              </CardDescription>
            </div>
          </div>
          <div
            className={`w-8 h-8 rounded-full bg-zinc-100 flex items-center justify-center transition-transform ${
              isExpanded ? "rotate-90" : ""
            }`}
          >
            <CaretRight size={18} className="text-zinc-500" />
          </div>
        </div>
      </CardHeader>

      {isExpanded && (
        <CardContent className="pt-0">
          <div className="border-t border-zinc-100 pt-6">
            {importResult ? (
              renderSuccessState()
            ) : (
              <>
                {renderStepIndicator()}
                {currentStep === 1 && renderUploadStep()}
                {currentStep === 2 && renderMatchStep()}
                {currentStep === 3 && renderPreviewStep()}
              </>
            )}
          </div>
        </CardContent>
      )}
    </Card>
  );
}
